/**
 * @fileoverview Module de gestion du prêt d'une BD.
 * Affiche un dialog HTML5 natif (<dialog>) permettant de saisir l'emprunteur
 * et la date du prêt. Fournit également un formateur pour l'affichage sur la carte.
 */

import { formatDate } from './utils.js';
import { openDialog, closeDialog } from './dialogs.js';

/**
 * Ouvre la popin de saisie d'un prêt et attend la décision de l'utilisateur.
 * Le dialog HTML5 natif gère automatiquement le focus trap et la fermeture par Échap.
 *
 * @returns {Promise<{ borrower: string, date: number } | null>}
 *   Résolution avec les valeurs saisies, ou `null` si l'utilisateur annule.
 */
export function openLoanDialog() {
    return new Promise((resolve) => {
        const dialog = document.getElementById('loan-dialog');
        const form = document.getElementById('loan-form');
        const inputBorrower = document.getElementById('input-loan-borrower');
        const inputDate = document.getElementById('input-loan-date');
        const btnCancel = document.getElementById('btn-loan-cancel');

        // Pré-remplit la date avec aujourd'hui (format YYYY-MM-DD attendu par input[type=date])
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        inputDate.value = `${yyyy}-${mm}-${dd}`;
        inputBorrower.value = '';

        /**
         * Détache les écouteurs ponctuels pour éviter les fuites mémoire
         * lors d'utilisations répétées du dialog.
         */
        function cleanup() {
            form.removeEventListener('submit', onSubmit);
            btnCancel.removeEventListener('click', onCancel);
            dialog.removeEventListener('cancel', onCancel); // Échap ou close natif
        }

        function onSubmit(event) {
            event.preventDefault();
            const borrower = inputBorrower.value.trim();
            const dateStr = inputDate.value;

            if (!borrower || !dateStr) return; // Le navigateur affiche déjà la validation HTML5

            // Conversion ISO (YYYY-MM-DD) → timestamp local minuit
            const date = new Date(dateStr).getTime();

            cleanup();
            closeDialog('loan-dialog');
            resolve({ borrower, date });
        }

        function onCancel() {
            cleanup();
            closeDialog('loan-dialog');
            resolve(null);
        }

        form.addEventListener('submit', onSubmit);
        btnCancel.addEventListener('click', onCancel);
        dialog.addEventListener('cancel', onCancel);

        openDialog('loan-dialog');
        // Focus initial sur le premier champ pour l'ergonomie clavier
        inputBorrower.focus();
    });
}

/**
 * Formate les informations de prêt pour affichage sur la carte.
 *
 * @param {{ borrower: string, date: number } | null | undefined} loan - Objet prêt.
 * @returns {string} Chaîne de la forme "Prêté à X le JJ/MM/AAAA", ou chaîne vide si pas de prêt.
 */
export function formatLoanInfo(loan) {
    if (!loan || !loan.borrower) return '';
    return `Prêté à ${loan.borrower} le ${formatDate(loan.date)}`;
}
